/**
 * Age calculation utilities shared across web dashboard pages.
 *
 * Canonical implementation — do not duplicate calculateAge in individual page files.
 */

/**
 * Calculate age in years from a date-of-birth string (ISO 8601 or YYYY-MM-DD).
 *
 * Returns -1 when the input is null, empty, unparseable, or IN THE FUTURE.
 *
 * -1 means "unknown", and unknown is not a number you can compare. In
 * particular it is NOT "old enough to need no consent".
 *
 * This docstring used to advise `age >= 0 && age < 13` and call it a way to
 * "naturally exclude unknown ages" — excluded from PROTECTION, which is the
 * wrong way round and is the shape that shipped in PlayerCreate: an unreadable
 * birthday rendered no parental-consent section at all, so nobody was asked.
 *
 * For a COPPA decision, treat -1 as needing consent, or reject the date and ask
 * again. Never let it fall through to the adult branch. For DISPLAY, `age >= 0`
 * is the right guard — showing "—" instead of "-1" harms nobody.
 */
export function calculateAge(dateOfBirth: string | null | undefined): number {
  if (!dateOfBirth) return -1;

  // C19 — parse the CALENDAR components, never `new Date(str)`.
  //
  // `new Date('2013-08-19')` is UTC midnight. Comparing that against a LOCAL
  // `today` makes `dob.getDate()` read as the PREVIOUS day anywhere west of UTC,
  // so the birthday test passes ~24h early and a 12-year-old reads as 13. A date
  // of birth is a calendar date, not an instant, so the fix is to stop treating
  // it as one. Measured at UTC-5 before the fix:
  //   '2013-08-19' -> parsed local Sun Aug 18 2013 19:00 -> getDate() 18, not 19.
  //
  // Accepts 'YYYY-MM-DD' and any ISO 8601 string that starts with it.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth);
  if (!m) return -1;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);

  // Reject impossible dates ('2013-02-30') the way the old Date parse did.
  const probe = new Date(year, month - 1, day);
  if (
    probe.getFullYear() !== year ||
    probe.getMonth() !== month - 1 ||
    probe.getDate() !== day
  ) {
    return -1;
  }

  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - (month - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age--;
  }
  return age;
}

/**
 * US Youth Soccer seasonal cutoff date — June 16.
 * Before June 16 = spring season (age group based on current calendar year).
 * On/after June 16 = fall season (age group based on next calendar year).
 */
const SEASON_CUTOFF_MONTH = 5; // June (0-indexed)
const SEASON_CUTOFF_DAY = 16;

/**
 * Calculate the current soccer age group (e.g., "U11", "U12") based on
 * birth year and the US Soccer seasonal cutoff (June 16).
 *
 * Spring season (before June 16): seasonYear = currentYear
 * Fall season (June 16+): seasonYear = currentYear + 1
 *
 * Age group = "U" + (seasonYear - birthYear)
 *
 * Example (today = March 22, 2026, spring season):
 *   Born 2015 → U11 (2026 - 2015 = 11)
 *   Born 2014 → U12 (2026 - 2014 = 12)
 *
 * Example (after June 16, 2026, fall season):
 *   Born 2015 → U12 (2027 - 2015 = 12)
 *   Born 2014 → U13 (2027 - 2014 = 13)
 */
export function calculateSoccerAgeGroup(dateOfBirth: string | null | undefined): string {
  if (!dateOfBirth) return '';
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return '';

  const today = new Date();
  const isFallSeason =
    today.getMonth() > SEASON_CUTOFF_MONTH ||
    (today.getMonth() === SEASON_CUTOFF_MONTH && today.getDate() >= SEASON_CUTOFF_DAY);

  const seasonYear = isFallSeason ? today.getFullYear() + 1 : today.getFullYear();
  const ageGroupNumber = seasonYear - dob.getFullYear();

  if (ageGroupNumber < 4 || ageGroupNumber > 19) return '';
  return `U${ageGroupNumber}`;
}

/**
 * Get the current season label (e.g., "Spring 2026" or "Fall 2026").
 */
export function getCurrentSeasonLabel(): string {
  const today = new Date();
  const isFallSeason =
    today.getMonth() > SEASON_CUTOFF_MONTH ||
    (today.getMonth() === SEASON_CUTOFF_MONTH && today.getDate() >= SEASON_CUTOFF_DAY);
  return isFallSeason ? `Fall ${today.getFullYear()}` : `Spring ${today.getFullYear()}`;
}
